import { parseActorAppearance } from "@neucleos/theme";
/**
 * Environment-scoped settings hooks.
 *
 * Abstracts the split between server-authoritative settings (persisted in
 * `settings.json` on the server, fetched via `server.getConfig`) and
 * client-only settings (persisted in localStorage).
 *
 * Live server settings always require an environment id. Primary-environment
 * access is intentionally named as such so environment-sensitive consumers
 * cannot silently read the wrong server's settings.
 */
import { useCallback, useContext, useMemo, useSyncExternalStore } from "react";
import { RegistryContext, useAtomValue } from "@effect/atom-react";
import {
  DEFAULT_SERVER_SETTINGS,
  type EnvironmentId,
  ServerSettings,
  ServerSettingsPatch,
} from "@cleo/contracts";
import {
  type ClientSettingsPatch,
  type ClientSettings,
  DEFAULT_CLIENT_SETTINGS,
  type EnvironmentIdentificationMode,
  type UnifiedSettings,
} from "@cleo/contracts/settings";
import { safeErrorLogAttributes } from "@cleo/client-runtime/errors";
import { applyServerSettingsPatch } from "@cleo/shared/serverSettings";
import { ensureLocalApi } from "~/localApi";
import {
  getThemeDefinition,
  getThemePreviewSidebarArtwork,
  resolveThemeHalf,
  subscribeToThemePreview,
  themeAllowsSidebarArtwork,
} from "~/themePalette";
import * as Cause from "effect/Cause";
import * as Struct from "effect/Struct";
import { AsyncResult } from "effect/unstable/reactivity";
import { toastManager } from "~/components/ui/toast";
import { primaryServerSettingsAtom, serverEnvironment } from "~/state/server";
import { usePrimaryEnvironment } from "~/state/environments";
import { useAtomCommand } from "~/state/use-atom-command";
import { useTheme } from "./useTheme";

const CLIENT_SETTINGS_PERSISTENCE_ERROR_SCOPE = "[CLIENT_SETTINGS]";

type UnifiedSettingsPatch = ServerSettingsPatch & ClientSettingsPatch;

const clientSettingsListeners = new Set<() => void>();
const clientSettingsHydrationListeners = new Set<() => void>();
let clientSettingsSnapshot = DEFAULT_CLIENT_SETTINGS;
let clientSettingsHydrated = false;
let clientSettingsHydrationPromise: Promise<void> | null = null;
let clientSettingsHydrationGeneration = 0;

function emitClientSettingsChange() {
  for (const listener of clientSettingsListeners) {
    listener();
  }
}

function emitClientSettingsHydrationChange() {
  for (const listener of clientSettingsHydrationListeners) {
    listener();
  }
}

function getClientSettingsSnapshot(): ClientSettings {
  return clientSettingsSnapshot;
}

function replaceClientSettingsSnapshot(settings: ClientSettings): void {
  clientSettingsSnapshot = settings;
  emitClientSettingsChange();
}

function setClientSettingsHydrated(nextHydrated: boolean): void {
  if (clientSettingsHydrated === nextHydrated) {
    return;
  }
  clientSettingsHydrated = nextHydrated;
  emitClientSettingsHydrationChange();
}

// Exported for cleoWide's held Context open (dated 2026-10-07), which must hear
// Widen turned on by any writer, Cleo's set_setting included.
export function subscribeClientSettings(listener: () => void): () => void {
  clientSettingsListeners.add(listener);
  void hydrateClientSettings();
  return () => {
    clientSettingsListeners.delete(listener);
  };
}

function getClientSettingsHydratedSnapshot(): boolean {
  return clientSettingsHydrated;
}

function subscribeClientSettingsHydration(listener: () => void): () => void {
  clientSettingsHydrationListeners.add(listener);
  void hydrateClientSettings();
  return () => {
    clientSettingsHydrationListeners.delete(listener);
  };
}

async function hydrateClientSettings(): Promise<void> {
  if (clientSettingsHydrated) {
    return;
  }
  if (clientSettingsHydrationPromise) {
    return clientSettingsHydrationPromise;
  }

  const hydrationGeneration = clientSettingsHydrationGeneration;
  const nextHydration = (async () => {
    try {
      const persistedSettings = await ensureLocalApi().persistence.getClientSettings();
      if (hydrationGeneration !== clientSettingsHydrationGeneration) {
        return;
      }
      if (persistedSettings) {
        replaceClientSettingsSnapshot({ ...DEFAULT_CLIENT_SETTINGS, ...persistedSettings });
      }
      watchExternalClientSettingsChanges();
    } catch (error) {
      console.error(`${CLIENT_SETTINGS_PERSISTENCE_ERROR_SCOPE} hydrate failed`, {
        operation: "hydrate",
        ...safeErrorLogAttributes(error),
      });
    } finally {
      if (hydrationGeneration === clientSettingsHydrationGeneration) {
        setClientSettingsHydrated(true);
      }
    }
  })();

  const hydrationPromise = nextHydration.finally(() => {
    if (clientSettingsHydrationPromise === hydrationPromise) {
      clientSettingsHydrationPromise = null;
    }
  });
  clientSettingsHydrationPromise = hydrationPromise;

  return clientSettingsHydrationPromise;
}

// Register §41 (D-US2): when another writer atomically replaces the Desk's
// client-settings file — today the server's gated settings door, acting on
// the person's request — the renderer takes the decoded document as its
// snapshot, so the change is felt without a restart. The bridge fires for
// the renderer's own writes too; replacing with an equal document is a
// no-op for React. Subscribed once, after the first hydration.
let externalClientSettingsUnsubscribe: (() => void) | null = null;

function watchExternalClientSettingsChanges(): void {
  if (externalClientSettingsUnsubscribe !== null) return;
  const subscribe = ensureLocalApi().persistence.onClientSettingsChange;
  if (typeof subscribe !== "function") return;
  externalClientSettingsUnsubscribe = subscribe((settings) => {
    replaceClientSettingsSnapshot({ ...DEFAULT_CLIENT_SETTINGS, ...settings });
  });
}

// W-8ecf392a: the window sends only the fields it changed, and the Desk
// writes them over what the file holds now. Another Desk process (the
// server's settings door, acting on the person's request) may have changed a
// field this window has not heard about yet; sending the whole snapshot would
// put that field's old value back.
export function persistClientSettings(patch: ClientSettingsPatch): void {
  replaceClientSettingsSnapshot({ ...getClientSettingsSnapshot(), ...patch });
  void ensureLocalApi()
    .persistence.patchClientSettings(patch)
    .catch((error) => {
      console.error(`${CLIENT_SETTINGS_PERSISTENCE_ERROR_SCOPE} persist failed`, {
        operation: "persist",
        ...safeErrorLogAttributes(error),
      });
    });
}

// ── Key sets for routing patches ─────────────────────────────────────

// A server patch also carries named operations that are not settings fields
// (account and World-binding removals, scoped-setting resets). They are the
// server's; filed as device preferences they would never reach it.
const SERVER_SETTINGS_KEYS = new Set<string>([
  ...Struct.keys(ServerSettings.fields),
  ...Struct.keys(ServerSettingsPatch.fields),
]);

export function splitSettingsPatch(patch: UnifiedSettingsPatch): {
  serverPatch: ServerSettingsPatch;
  clientPatch: ClientSettingsPatch;
} {
  const serverPatch: Record<string, unknown> = {};
  const clientPatch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(patch)) {
    if (SERVER_SETTINGS_KEYS.has(key)) {
      serverPatch[key] = value;
    } else {
      clientPatch[key] = value;
    }
  }
  return {
    serverPatch: serverPatch as ServerSettingsPatch,
    clientPatch: clientPatch as ClientSettingsPatch,
  };
}

// ── Hooks ────────────────────────────────────────────────────────────

/**
 * Non-hook accessor for the current merged client settings snapshot.
 * Used by non-React code paths (e.g. runtime services) that need the latest
 * settings without subscribing.
 */
export function getClientSettings(): ClientSettings {
  return getClientSettingsSnapshot();
}

export function useClientSettingsHydrated(): boolean {
  return useSyncExternalStore(
    subscribeClientSettingsHydration,
    getClientSettingsHydratedSnapshot,
    () => false,
  );
}

function useClientSettingsValue(): ClientSettings {
  return useSyncExternalStore(
    subscribeClientSettings,
    getClientSettingsSnapshot,
    () => DEFAULT_CLIENT_SETTINGS,
  );
}

export function mergeEnvironmentSettings(
  serverSettings: ServerSettings,
  clientSettings: ClientSettings,
): UnifiedSettings {
  return { ...serverSettings, ...clientSettings };
}

function useMergedSettings<T>(
  serverSettings: ServerSettings,
  selector: ((settings: UnifiedSettings) => T) | undefined,
): T {
  const clientSettings = useClientSettingsValue();

  const merged = useMemo<UnifiedSettings>(
    () => mergeEnvironmentSettings(serverSettings, clientSettings),
    [clientSettings, serverSettings],
  );

  return useMemo(() => (selector ? selector(merged) : (merged as T)), [merged, selector]);
}

export function useClientSettings<T = ClientSettings>(
  selector?: (settings: ClientSettings) => T,
): T {
  const settings = useClientSettingsValue();
  return useMemo(() => (selector ? selector(settings) : (settings as T)), [selector, settings]);
}

export function resolveEnvironmentIdentificationMode(input: {
  mode: EnvironmentIdentificationMode;
  settingsHydrated: boolean;
  paletteThemeActive?: boolean;
  paletteThemeAllowsArtwork?: boolean;
}): EnvironmentIdentificationMode {
  // Avoid briefly rendering the default artwork before a persisted pill/none choice loads.
  if (!input.settingsHydrated) return "none";
  // Artwork palettes are maintained for built-ins only. Keep an explicit
  // "none", but use the theme-aware pill for user-controlled palettes.
  return input.paletteThemeActive && !input.paletteThemeAllowsArtwork && input.mode === "artwork"
    ? "pill"
    : input.mode;
}

export function useEnvironmentIdentificationMode(): EnvironmentIdentificationMode {
  const settingsHydrated = useClientSettingsHydrated();
  const mode = useClientSettingsValue().environmentIdentificationMode;
  const { resolvedTheme, theme, themeHalves } = useTheme();
  const previewSidebarArtwork = useSyncExternalStore(
    subscribeToThemePreview,
    getThemePreviewSidebarArtwork,
    () => null,
  );
  const activeTheme = resolveThemeHalf(theme, themeHalves, resolvedTheme);
  const activeThemeDefinition = getThemeDefinition(activeTheme);
  return resolveEnvironmentIdentificationMode({
    mode,
    settingsHydrated,
    paletteThemeActive: previewSidebarArtwork !== null || activeThemeDefinition !== null,
    paletteThemeAllowsArtwork: previewSidebarArtwork ?? themeAllowsSidebarArtwork(activeTheme),
  });
}

/** Read current settings for one environment, merged with client-local preferences. */
export function useEnvironmentSettings<T = UnifiedSettings>(
  environmentId: EnvironmentId,
  selector?: (settings: UnifiedSettings) => T,
): T {
  const serverSettings = useAtomValue(serverEnvironment.settingsValueAtom(environmentId));
  return useMergedSettings(serverSettings ?? DEFAULT_SERVER_SETTINGS, selector);
}

/** Primary-only settings access for the settings UI and other explicitly global surfaces. */
export function usePrimarySettings<T = UnifiedSettings>(
  selector?: (settings: UnifiedSettings) => T,
): T {
  return useMergedSettings(useAtomValue(primaryServerSettingsAtom), selector);
}

const REFUSED_WITHOUT_REASON = "The server did not accept this change.";

/**
 * What to tell the person when the server refuses a settings change, or null
 * when there is nothing to tell. The server's own reason rides in the error's
 * cause; its top-level message names the settings file path, which helps nobody.
 * 19 September 2026: refusals went to the developer console only, so eight
 * refused deletes in the installed Desk looked like nothing happening.
 */
export function describeSettingsRefusal(
  result: AsyncResult.AsyncResult<unknown, unknown>,
): string | null {
  if (!AsyncResult.isFailure(result) || Cause.hasInterruptsOnly(result.cause)) return null;
  const error = Cause.squash(result.cause) as
    | { message?: unknown; cause?: { message?: unknown } }
    | undefined;
  const fromCause = typeof error?.cause?.message === "string" ? error.cause.message.trim() : "";
  if (fromCause) return fromCause;
  const own = typeof error?.message === "string" ? error.message.trim() : "";
  return own && !/^Server settings \w[\w-]* failed/.test(own) ? own : REFUSED_WITHOUT_REASON;
}

export interface SettingsUpdateNotice {
  /** "no": the server refused. "unknown": the request broke while reading the reply. */
  readonly saved: "no" | "unknown";
  readonly title: string;
  readonly description: string;
}

/** The two answers the server declares for a settings change (WsServerUpdateSettingsRpc). Both are given before anything is written. */
const SERVER_REFUSALS = new Set(["ServerSettingsError", "EnvironmentAuthorizationError"]);

/**
 * What to show the person. "Not saved" is a statement about the server's file,
 * so only the server's own refusal may make it. Anything else means the app did
 * not get the server's answer and the change may be on disk: a defect while
 * reading the reply (19 September 2026, three accepted account adds), or a
 * connection that dropped after the request was sent. The cause is always put
 * into words.
 */
export function settingsUpdateNotice(
  result: AsyncResult.AsyncResult<unknown, unknown>,
): SettingsUpdateNotice | null {
  if (!AsyncResult.isFailure(result) || Cause.hasInterruptsOnly(result.cause)) return null;
  const defect = Cause.squash(result.cause);
  const tag = (defect as { _tag?: unknown } | null | undefined)?._tag;
  if (!Cause.hasDies(result.cause) && typeof tag === "string" && SERVER_REFUSALS.has(tag)) {
    return {
      saved: "no",
      title: "That change was not saved",
      description: describeSettingsRefusal(result) ?? REFUSED_WITHOUT_REASON,
    };
  }
  const words = Cause.pretty(result.cause).replace(/\s+/g, " ").trim();
  const spoken =
    words && !/\[object Object\]/.test(words)
      ? words
      : (() => {
          try {
            return JSON.stringify(defect);
          } catch {
            return String(defect);
          }
        })();
  return {
    saved: "unknown",
    title: "The app could not read the server's answer",
    description: `This change may have been saved. Reopen this page to see its real state. Cause: ${String(spoken).slice(0, 300)}`,
  };
}

/** What a change named, reduced to what reading back can compare: never the owner the server stamps, never a credential's value. */
function namedByPatch(
  settings: ServerSettings,
  patch: ServerSettingsPatch,
): Record<string, unknown> {
  const named: Record<string, unknown> = {};
  const from = settings as unknown as Record<string, Record<string, unknown> | undefined>;
  for (const [key, value] of Object.entries(patch)) {
    if (key === "providerInstances") {
      named[key] = Object.keys(value as object).map((id) => {
        const instance = settings.providerInstances[id as keyof typeof settings.providerInstances];
        if (instance === undefined) return [id, null];
        const { ownerActorId: _owner, environment, ...rest } = instance;
        return [
          id,
          {
            ...rest,
            environment: environment?.map((entry) =>
              entry.sensitive ? { name: entry.name } : entry,
            ),
          },
        ];
      });
    } else if (key.endsWith("Removals")) {
      const held = from[key.replace(/Removals$/, "s")] ?? {};
      named[key] = (value as ReadonlyArray<string>).map((id) => id in held);
    } else named[key] = from[key];
  }
  return named;
}

const sameValue = (left: unknown, right: unknown): boolean => {
  if (Object.is(left, right)) return true;
  if (typeof left !== "object" || typeof right !== "object" || left === null || right === null)
    return false;
  if (Array.isArray(left) !== Array.isArray(right)) return false;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    const [a, b] = [
      (left as Record<string, unknown>)[key],
      (right as Record<string, unknown>)[key],
    ];
    if (a === undefined && b === undefined) continue;
    if (!sameValue(a, b)) return false;
  }
  return true;
};

/**
 * Whether a change whose answer was lost is on the server, judged from the
 * settings held before it and the settings read back after it. Only what the
 * change named is compared, so another account changing meanwhile does not
 * decide it. "unknown" is a real answer: a change with nothing visible to
 * compare (a credential's value) cannot be told apart by reading.
 */
export function settledByReadBack(
  before: ServerSettings,
  after: ServerSettings,
  patch: ServerSettingsPatch,
): "yes" | "no" | "unknown" {
  const held = namedByPatch(before, patch);
  if (!sameValue(held, namedByPatch(after, patch))) return "yes";
  return sameValue(held, namedByPatch(applyServerSettingsPatch(before, patch), patch))
    ? "unknown"
    : "no";
}

export type SettingsUpdateOutcome =
  | { readonly ok: true }
  | { readonly ok: false; readonly saved: "no" | "unknown"; readonly reason: string };

/**
 * Returns an updater that routes each key to the correct backing store.
 *
 * Server keys are persisted via RPC and the view follows the server's change
 * stream; nothing is applied optimistically. A refused change is always shown
 * to the person, and the returned promise says whether it was accepted, so a
 * caller never announces success before the server has answered. Client keys
 * go through client persistence.
 */
function useUpdateSettingsTarget(environmentId: EnvironmentId | null) {
  const persistServerSettings = useAtomCommand(
    serverEnvironment.updateSettings,
    "server settings update",
  );
  const readServerSettings = useAtomCommand(serverEnvironment.getSettings, {
    label: "server settings read-back",
    reportFailure: false,
    reportDefect: false,
  });
  const registry = useContext(RegistryContext);
  const updateSettings = useCallback(
    async (patch: UnifiedSettingsPatch): Promise<SettingsUpdateOutcome> => {
      const { serverPatch, clientPatch } = splitSettingsPatch(patch);

      if (Object.keys(clientPatch).length > 0) {
        persistClientSettings(clientPatch);
      }
      if (Object.keys(serverPatch).length === 0 || !environmentId) return { ok: true };
      const before = registry.get(serverEnvironment.settingsValueAtom(environmentId));
      const result = await persistServerSettings({ environmentId, input: { patch: serverPatch } });
      if (AsyncResult.isSuccess(result)) {
        // The reply is the saved state; the screen shows it without waiting for the push.
        serverEnvironment.adoptSettingsAnswer(registry, environmentId, result.value);
        return { ok: true };
      }
      let notice = settingsUpdateNotice(result);
      if (notice === null) return { ok: true };
      if (notice.saved === "unknown") {
        // The answer was lost, not the server: ask it what it holds, show that,
        // and say which it was instead of sending the person to reopen the page.
        const readBack = await readServerSettings({ environmentId, input: {} });
        if (AsyncResult.isSuccess(readBack)) {
          serverEnvironment.adoptSettingsAnswer(registry, environmentId, readBack.value);
          const settled =
            before === null ? "unknown" : settledByReadBack(before, readBack.value, serverPatch);
          if (settled === "yes") return { ok: true };
          notice =
            settled === "no"
              ? {
                  saved: "no",
                  title: "That change was not saved",
                  description: "The server never received it. Nothing changed; try again.",
                }
              : {
                  saved: "unknown",
                  title: "The app could not confirm this change",
                  description:
                    "This page now shows what the server holds. Check it before trying again.",
                };
        }
      }
      toastManager.add({ type: "error", title: notice.title, description: notice.description });
      return { ok: false, saved: notice.saved, reason: notice.description };
    },
    [environmentId, persistServerSettings, readServerSettings, registry],
  );

  return updateSettings;
}

export function useUpdateEnvironmentSettings(environmentId: EnvironmentId) {
  return useUpdateSettingsTarget(environmentId);
}

export function useUpdatePrimarySettings() {
  return useUpdateSettingsTarget(usePrimaryEnvironment()?.environmentId ?? null);
}

export function useUpdateClientSettings() {
  return useCallback((patch: ClientSettingsPatch) => {
    persistClientSettings(patch);
  }, []);
}

export function __resetClientSettingsPersistenceForTests(): void {
  clientSettingsHydrationGeneration += 1;
  clientSettingsSnapshot = DEFAULT_CLIENT_SETTINGS;
  clientSettingsHydrated = false;
  clientSettingsHydrationPromise = null;
  clientSettingsListeners.clear();
  clientSettingsHydrationListeners.clear();
}

export function __setClientSettingsForTests(settings: ClientSettings): void {
  clientSettingsHydrationGeneration += 1;
  clientSettingsSnapshot = settings;
  clientSettingsHydrated = true;
  clientSettingsHydrationPromise = null;
}

/** Adopt only Actor-owned inputs; device typography and other preferences remain intact. */
export function adoptActorAppearance(preferences: unknown): void {
  persistClientSettings(parseActorAppearance(preferences));
}
